import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { statusMeta, dispatchMeta, formatMoney } from "@/lib/quotes";
import { Loader2, TrendingUp, TrendingDown, Award, Truck, Filter, ArrowRight, ChevronRight } from "lucide-react";
import { toast } from "sonner";

function firstDayOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const STATUS_ORDER = ["sent", "in_progress", "won", "lost"];
const DISPATCH_ORDER = ["pending", "packed", "ready_to_dispatch", "in_route", "completed"];

export default function QuotesDashboardPage() {
  const [startDate, setStartDate] = useState(firstDayOfMonth());
  const [endDate, setEndDate] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      const { data } = await api.get(`/quotes/dashboard/stats?${params.toString()}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const maxWon = useMemo(() => {
    if (!data?.leaderboard) return 0;
    return Math.max(1, ...data.leaderboard.map((r) => r.won_value || 0));
  }, [data]);

  // Build a link that preserves the current date range and pushes extra filters
  const filterHref = (extra = {}) => {
    const p = new URLSearchParams({ start_date: startDate, end_date: endDate, ...extra });
    return `/quotes?${p.toString()}`;
  };

  const kpiCards = [
    { key: "total_quotes", label: "Quotations", value: (d) => (d?.total_quotes ?? 0), color: "hsl(var(--foreground))", href: filterHref() },
    { key: "total_value", label: "Pipeline value", value: (d) => formatMoney(d?.total_value || 0), color: "hsl(var(--foreground))", href: filterHref() },
    { key: "won_value", label: "Won value", value: (d) => formatMoney(d?.won_value || 0), color: "hsl(var(--status-present))", href: filterHref({ status: "won" }) },
    { key: "win_rate", label: "Win rate", value: (d) => `${d?.win_rate || 0}%`, color: "hsl(var(--foreground))", href: filterHref({ status: "won" }) },
  ];

  return (
    <div className="px-4 pt-4 pb-24 space-y-6" data-testid="quotes-dashboard-page">
      <div>
        <div className="font-mono-mini text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Dashboard</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Sales & dispatch</h1>
        <p className="text-xs text-muted-foreground mt-1">Tap any tile to drill into the underlying quotations.</p>
      </div>

      {/* Filters */}
      <Card className="p-4 rounded-2xl border-border shadow-soft">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">From</Label>
            <Input
              type="date"
              data-testid="dash-start-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mt-1.5 h-10 font-mono-mini w-[150px]"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">To</Label>
            <Input
              type="date"
              data-testid="dash-end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="mt-1.5 h-10 font-mono-mini w-[150px]"
            />
          </div>
          <Button onClick={load} disabled={loading} className="h-10 btn-lift" data-testid="dash-apply-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Filter className="h-4 w-4 mr-2" />}
            Apply
          </Button>
        </div>
      </Card>

      {/* KPI cards — clickable */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpiCards.map((k) => (
          <Link
            key={k.key}
            to={k.href}
            data-testid={`kpi-${k.key}`}
            className="p-4 rounded-md border border-border bg-card btn-lift block hover:shadow-[0_2px_0_hsl(var(--border))] transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{k.label}</div>
              <ChevronRight className="h-3 w-3 text-muted-foreground opacity-60" />
            </div>
            <div className="font-display text-3xl font-bold tabular-nums mt-2" style={{ color: k.color }}>
              {loading ? "—" : k.value(data)}
            </div>
          </Link>
        ))}
      </div>

      {/* Funnel by status — clickable */}
      <Card className="p-4 rounded-2xl border-border shadow-soft">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">By status</div>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">tap to open</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {STATUS_ORDER.map((sid) => {
            const s = statusMeta(sid);
            const stat = data?.by_status?.[sid] || { count: 0, value: 0 };
            return (
              <Link
                key={sid}
                to={filterHref({ status: sid })}
                data-testid={`funnel-${sid}`}
                className="p-3 rounded-md border border-border btn-lift block hover:shadow-[0_2px_0_hsl(var(--border))] transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-widest" style={{ color: s.color }}>{s.label}</div>
                  <ChevronRight className="h-3 w-3 text-muted-foreground opacity-60" />
                </div>
                <div className="font-display text-2xl font-bold tabular-nums mt-1">{stat.count}</div>
                <div className="text-[11px] text-muted-foreground font-mono-mini tabular-nums mt-1">
                  {formatMoney(stat.value)}
                </div>
              </Link>
            );
          })}
        </div>
      </Card>

      {/* Leaderboard — clickable rows */}
      <Card className="p-4 rounded-2xl border-border shadow-soft" data-testid="leaderboard">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Sales leaderboard</div>
          <Award className="h-4 w-4 text-muted-foreground" />
        </div>
        {loading ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" /> Loading…
          </div>
        ) : (data?.leaderboard?.length ?? 0) === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">No quotations in this range yet.</div>
        ) : (
          <ol className="space-y-3">
            {data.leaderboard.map((row, idx) => {
              const pct = Math.round(((row.won_value || 0) / maxWon) * 100);
              return (
                <li key={row.user_id || idx}>
                  <Link
                    to={filterHref({ user_id: row.user_id || "" })}
                    data-testid={`leaderboard-row-${row.user_id || idx}`}
                    className="block px-2 py-2 -mx-2 rounded-md hover:bg-black/[0.03] transition-colors"
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-mono-mini text-[10px] w-4 text-muted-foreground">#{idx + 1}</span>
                        <span className="text-sm font-medium truncate">{row.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-mono-mini tabular-nums text-sm">
                          <span style={{ color: "hsl(var(--status-present))" }}>{formatMoney(row.won_value)}</span>
                          <span className="text-muted-foreground text-[11px]"> / {formatMoney(row.value)}</span>
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                          {row.quotes} quotes · {row.win_rate}% win
                        </div>
                      </div>
                    </div>
                    <div className="mt-1.5 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full transition-all" style={{ width: `${pct}%`, background: "hsl(var(--status-present))" }} />
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-[10px] uppercase tracking-widest text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><TrendingUp className="h-2.5 w-2.5" style={{ color: "hsl(var(--status-present))" }} /> {row.won}</span>
                      <span className="inline-flex items-center gap-1"><TrendingDown className="h-2.5 w-2.5" style={{ color: "hsl(var(--status-absent))" }} /> {row.lost}</span>
                      <span className="ml-auto inline-flex items-center gap-1">See quotes <ArrowRight className="h-2.5 w-2.5" /></span>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ol>
        )}
      </Card>

      {/* Dispatch overview — clickable */}
      <Card className="p-4 rounded-2xl border-border shadow-soft">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground">
            <Truck className="h-4 w-4" /> Dispatch overview
          </div>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">tap to open</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {DISPATCH_ORDER.map((did) => {
            const d = dispatchMeta(did);
            const count = data?.dispatch?.[did] ?? 0;
            return (
              <Link
                key={did}
                to={filterHref({ view: "dispatch", dispatch_status: did })}
                data-testid={`dispatch-count-${did}`}
                className="p-3 rounded-md border border-border btn-lift block hover:shadow-[0_2px_0_hsl(var(--border))] transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-widest" style={{ color: d.color }}>{d.label}</div>
                  <ChevronRight className="h-3 w-3 text-muted-foreground opacity-60" />
                </div>
                <div className="font-display text-2xl font-bold tabular-nums mt-1">{count}</div>
              </Link>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
